<template>
  <header class="nav">
    <div class="nav-inner lp-container">
      <router-link to="/" class="brand">
        <img src="../../assets/nukli_logo.png" alt="Nukli" class="brand-logo" />
        <span class="brand-name">Nukli</span>
      </router-link>

      <nav class="nav-links">
        <button v-for="(item, i) in menuItems" :key="i" class="nav-link" @click="scrollToSection(item)">{{ item.text
          }}</button>
      </nav>

      <div class="nav-actions">
        <language-selector class="nav-lang"></language-selector>

        <button class="icon-btn" :aria-label="$t('nav.signIn')" @click="toggleTheme">
          <l-icon size="19" v-if="$vuetify.theme.dark">mdi-weather-sunny</l-icon>
          <l-icon size="19" v-else>mdi-weather-night</l-icon>
        </button>

        <!-- La app web es la otra puerta de entrada al producto, no un enlace de
             menú: va como botón secundario, con el mismo texto que el CTA del
             hero ("Ábrela en el navegador") para que se entienda qué abre. -->
        <a class="lp-btn lp-btn--ghost nav-webapp d-none d-md-inline-flex" :href="webAppUrl">
          <l-icon size="17">globe</l-icon>
          {{ $t('nav.openWebApp') }}
        </a>

        <!-- A /download y no al binario directo: ahora hay build para macOS,
             Windows y Linux, y la página resuelve cuál toca. -->
        <router-link class="lp-btn lp-btn--primary nav-cta" to="/download">
          {{ $t('nav.getStarted') }}
        </router-link>

        <!-- Avatar (o "Iniciar sesión" si no hay sesión). En móvil el propio
             componente esconde el botón de texto, pero el avatar se queda: es
             la única señal de que hay sesión iniciada. -->
        <login-button class="nav-login"></login-button>

        <button class="icon-btn nav-burger" aria-label="Menu" @click="$emit('toggleDrawer')">
          <l-icon size="20">mdi-menu</l-icon>
        </button>
      </div>
    </div>
  </header>
</template>

<script>
import LoginButton from '../../views/profile/login/LoginButton';
import LanguageSelector from '../LanguageSelector.vue';
import { WEB_APP_URL } from '@/constants/download';

export default {
  name: 'AppBar',
  components: { LoginButton, LanguageSelector },
  data() {
    return { webAppUrl: WEB_APP_URL };
  },
  computed: {
    menuItems() {
      return [
        { text: this.$t('nav.features'), id: 'features' },
        { text: this.$t('nav.pricing'), id: 'pricing' },
        { text: this.$t('nav.testimonials'), id: 'testimonials' },
        { text: this.$t('nav.guides'), id: 'guides', route: '/guides' },
        { text: this.$t('nav.blog'), id: 'blog', route: '/blog' },
      ];
    },
  },
  methods: {
    scrollToSection(item) {
      if (item.route) {
        this.$router.push(item.route);
        return;
      }
      if (this.$route.name !== 'Home') {
        this.$router.push('/').then(() => {
          setTimeout(() => this.scrollTo(item.id), 300);
        });
      } else {
        this.scrollTo(item.id);
      }
    },
    scrollTo(id) {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    },
    toggleTheme() {
      this.$root.isDark = !this.$root.isDark;
      this.$vuetify.theme.dark = this.$root.isDark;
      this.$cookies.set('darkMode', this.$root.isDark);
    },
  },
};
</script>

<style scoped>
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: 64px;
  background: rgba(255, 255, 255, 0.8);
  background: color-mix(in srgb, var(--lp-bg) 80%, transparent);
  backdrop-filter: saturate(180%) blur(16px);
  -webkit-backdrop-filter: saturate(180%) blur(16px);
  border-bottom: 1px solid var(--lp-line);
}

.nav-inner {
  height: 64px;
  display: flex;
  align-items: center;
  gap: 24px;
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  text-decoration: none;
}

.brand-logo {
  width: 24px;
  height: 24px;
  object-fit: contain;
  border: 1px solid var(--lp-line);
  border-radius: 8px;
  background: var(--lp-bg);
  box-shadow: 0 0 3px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  &:hover {
    transform: scale(1.05);
  }
}

.brand-name {
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 700;
  font-size: 1.2rem;
  letter-spacing: -0.02em;
  color: var(--lp-ink);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-left: 12px;
}

/* El contenedor está topado a 1120 px: cuando los enlaces caben, no hay más
   ancho que ganar ensanchando la ventana. De ahí el aire justo — con el botón
   de la app web escrito entero, el ucraniano es el que menos margen deja. */
.nav-link {
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  font-family: 'Space Grotesk', sans-serif;
  font-size: 0.925rem;
  font-weight: 500;
  color: var(--lp-muted);
  transition: color 0.15s ease, background 0.15s ease;
}

.nav-link:hover {
  color: var(--lp-ink);
  background: var(--lp-bg-subtle);
}

/* El grupo de acciones va pegado a la derecha SIEMPRE. Antes el empujón venía
   del `margin-right: auto` de .nav-links, así que al plegarse el menú (≤1159px)
   desaparecía con él y el idioma / la luna / la hamburguesa acababan pegados al
   logo. Con `margin-left: auto` aquí, no depende de que el menú exista. */
.nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.icon-btn {
  width: 38px;
  height: 38px;
  border-radius: 9px;
  border: 1px solid transparent;
  background: transparent;
  color: var(--lp-ink-2);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.15s ease;
}

.icon-btn:hover {
  background: var(--lp-bg-subtle);
}

.nav-cta {
  height: 38px;
  padding: 0 16px;
  font-size: 0.9rem;
}

/* Botón a la app web (mail.nukliapp.com). Secundario: pesa menos que el CTA de
   descarga, pero se lee como botón y no como un enlace más del menú. */
.nav-webapp {
  height: 38px;
  padding: 0 13px;
  font-size: 0.875rem;
  gap: 6px;
  text-decoration: none;
  white-space: nowrap;
}

.nav-burger {
  display: none;
}

/* Los enlaces del menú se pliegan en la hamburguesa antes que en Vuetify (959):
   con el botón de la app web escrito entero ("Ouvrir dans le navigateur" mide
   223 px en francés) la barra desbordaba entre 960 y 1100. */
@media (max-width: 1159px) {
  .nav-links {
    display: none;
  }

  .nav-burger {
    display: inline-flex;
  }
}

@media (max-width: 599px) {
  .nav-cta {
    display: none;
  }
}
</style>
